import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XIcon, CheckCircleIcon, UsersIcon, PhoneIcon, ClipboardCheckIcon, MessageSquareIcon, TrendingUpIcon, AlertCircleIcon, LightbulbIcon, AwardIcon, ArrowRightIcon, ArrowLeftIcon, UserCheckIcon, CalendarIcon, FileTextIcon, TargetIcon, StarIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
type QuestStep = {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  gradient: string;
  content: {
    tips: string[];
    warnings?: string[];
    checklist?: string[];
  };
};
export function LeadsTutorial() {
  const navigate = useNavigate();
  const [currentQuest, setCurrentQuest] = useState(0);
  const [completedQuests, setCompletedQuests] = useState<string[]>([]);
  const quests: QuestStep[] = [{
    id: 'intro',
    title: 'Bun venit în Ghidul Leadurilor',
    description: 'Învață cum să gestionezi și să califici leaduri eficient pentru conversie maximă',
    icon: UsersIcon,
    color: 'blue',
    gradient: 'from-blue-500 to-indigo-600',
    content: {
      tips: ['Leadurile sunt potențiali clienți care au arătat interes pentru proprietățile tale', 'Un lead bine calificat are șanse de 5x mai mari să devină client', 'Sistemul nostru te ghidează prin fiecare etapă a procesului de calificare', 'Vei învăța tehnici profesionale de apelare și calificare', 'Timpul de răspuns este critic - contactează leadurile în maxim 5 minute']
    }
  }, {
    id: 'lead-sources',
    title: 'Surse de Leaduri & Prioritizare',
    description: 'Înțelege de unde vin leadurile și cum să le prioritizezi',
    icon: TargetIcon,
    color: 'emerald',
    gradient: 'from-emerald-500 to-green-600',
    content: {
      tips: ['🔥 HOT LEADS (Prioritate 1): Leaduri noi din ultimele 5 minute - contactează IMEDIAT', '🟠 WARM LEADS (Prioritate 2): Leaduri din ultimele 24h - contactează în aceeași zi', '🔵 COLD LEADS (Prioritate 3): Leaduri mai vechi de 24h - programează follow-up', '📱 Surse: Site propriu, OLX, Storia, Facebook, Recomandări', '⭐ Leadurile din recomandări au rata de conversie de 60%+', '📊 Leadurile din site au rata de conversie de 30-40%', '🎯 Leadurile din OLX/Storia au rata de conversie de 15-25%'],
      warnings: ['NU ignora leadurile noi - fiecare minut pierdut reduce șansele de conversie cu 10%', 'NU trata toate leadurile la fel - prioritizează după sursă și vârstă'],
      checklist: ['Verifică sursa leadului (site, OLX, recomandare)', 'Notează ora primirii leadului', 'Prioritizează după vârstă: <5min > <24h > >24h', 'Pregătește script de apelare în funcție de sursă']
    }
  }, {
    id: 'first-call',
    title: 'Primul Apel - Tehnici de Contact',
    description: 'Cum să conduci primul apel pentru a crea o impresie excelentă',
    icon: PhoneIcon,
    color: 'purple',
    gradient: 'from-purple-500 to-pink-600',
    content: {
      tips: ['📞 Sună în maxim 5 minute de la primirea leadului (ideal: 1-2 minute)', '😊 Zâmbește când vorbești - se aude în voce și creează energie pozitivă', '🎯 Prezentare scurtă: "Bună ziua, sunt [Nume] de la [Agenție]. V-am sunat în legătură cu cererea dumneavoastră pentru apartament."', '❓ Pune întrebări deschise: "Ce anume căutați?", "Cum vi se pare zona?"', '👂 Ascultă activ - 70% ascultare, 30% vorbire', '📝 Notează TOTUL: preferințe, obiecții, disponibilitate', '⏰ Stabilește următorul pas concret: "Vă sun mâine la ora 14 cu 3 opțiuni"'],
      warnings: ['NU începe cu "Deranjez?" - sună nesigur și neprofesionist', 'NU citești din script ca un robot - fii natural și empatic', 'NU vorbi despre tine sau agenție mai mult de 10 secunde', 'NU întrebi "Când aveți timp?" - propune tu un interval concret'],
      checklist: ['Verifică că ai liniște și semnal bun înainte de apel', 'Ai CRM-ul deschis pentru a nota în timp real', 'Zâmbește înainte să apeși "Call"', 'Prezintă-te profesional (nume + agenție)', 'Confirmă că e momentul potrivit pentru discuție', 'Pune 3-5 întrebări de calificare', 'Stabilește următorul pas concret', 'Indexează apelul IMEDIAT după închidere']
    }
  }, {
    id: 'qualification',
    title: 'Calificarea Leadului',
    description: 'Cum să identifici rapid dacă leadul e serios și potrivit',
    icon: UserCheckIcon,
    color: 'amber',
    gradient: 'from-amber-500 to-orange-600',
    content: {
      tips: ['💰 BUGET: "Care e bugetul dumneavoastră lunar pentru chirie?" (întrebare directă)', '📍 ZONĂ: "În ce zonă preferați? De ce această zonă?" (înțelege motivația)', '📅 TERMEN: "Când doriți să vă mutați?" (urgența = prioritate)', '👥 OCUPANȚI: "Câți veți locui? Aveți animale?" (compatibilitate proprietăți)', '🏢 ANGAJARE: "Unde lucrați? Contract permanent?" (stabilitate financiară)', '🔍 MOTIVAȚIE: "De ce vă mutați?" (înțelege contextul)', '✅ Lead CALIFICAT = Buget realist + Zonă clară + Termen <30 zile + Angajare stabilă'],
      warnings: ['NU accepta răspunsuri vagi: "Depinde", "Nu știu exact" - insistă politicos', 'NU continua cu leaduri necalificate - pierzi timp prețios', 'NU judeca după voce sau nume - fii profesionist cu toți'],
      checklist: ['Buget: Minim - Maxim (EUR)', 'Zonă preferată + alternative acceptabile', 'Termen mutare: Urgent (<7 zile), Normal (<30 zile), Flexibil (>30 zile)', 'Nr. camere: 1, 2, 3, 4+', 'Ocupanți: Adulți + Copii + Animale', 'Situație profesională: Angajat / Freelancer / Student / Pensionar', 'Motivație mutare: Serviciu nou / Familie / Upgrade / Altele']
    }
  }, {
    id: 'objections',
    title: 'Gestionarea Obiecțiilor',
    description: 'Cum să răspunzi la obiecții și să menții leadul interesat',
    icon: MessageSquareIcon,
    color: 'rose',
    gradient: 'from-rose-500 to-pink-600',
    content: {
      tips: ['💬 "E prea scump" → "Înțeleg. Să vedem ce include prețul: utilități, parcare, mobilat complet. Comparativ cu altele din zonă, e chiar competitiv."', '📞 "Vreau să mă gândesc" → "Perfect! Despre ce anume vreți să vă gândiți? Poate vă pot ajuta cu informații suplimentare acum."', '🏠 "Vreau să văd mai multe opțiuni" → "Excelent! V-am pregătit deja 3 variante similare. Le putem vedea împreună mâine la 14?"', '⏰ "Nu am timp acum" → "Înțeleg perfect. Când ați avea 10 minute pentru o discuție scurtă? Mâine dimineață sau după-amiază?"', '🤔 "Mă mai gândesc" → "Sigur! Între timp, vă trimit detaliile pe WhatsApp. Vă sun joi să vedem ce ați decis?"', '💡 Tehnica FFF: Feel (Înțeleg cum vă simțiți) → Felt (Alții s-au simțit la fel) → Found (Au descoperit că...)'],
      warnings: ['NU te certa sau contrazici clientul direct - validează mai întâi emoția', 'NU renunța după prima obiecție - majoritatea clienților obiectează 2-3 ori', 'NU lua obiecțiile personal - sunt parte normală a procesului'],
      checklist: ['Ascultă obiecția complet fără să întrerupi', 'Validează emoția: "Înțeleg perfect..."', 'Pune întrebare clarificatoare: "Despre ce anume vă referiți?"', 'Oferă soluție sau alternativă concretă', 'Confirmă că obiecția e rezolvată', 'Stabilește următorul pas']
    }
  }, {
    id: 'follow-up',
    title: 'Follow-up & Persistență',
    description: 'Strategii de urmărire pentru conversie maximă',
    icon: CalendarIcon,
    color: 'indigo',
    gradient: 'from-indigo-500 to-blue-600',
    content: {
      tips: ['📅 REGULA 7 TOUCH-POINTS: Majoritatea conversiilor apar după 5-7 contacte', '📞 Apel 1: Imediat (calificare inițială)', '📱 SMS/WhatsApp: După 2 ore (confirmare + link proprietăți)', '📞 Apel 2: A doua zi (prezentare opțiuni + programare vizionare)', '📧 Email: După 3 zile (detalii suplimentare + testimoniale)', '📞 Apel 3: După 5 zile (check-in + noi opțiuni)', '📱 WhatsApp: După 7 zile (reminder + urgență)', '📞 Apel Final: După 10 zile (ultimă șansă + ofertă specială)', '⏰ Cel mai bun timp pentru apeluri: 10-12 și 17-19'],
      warnings: ['NU suni mai mult de 1x pe zi - devine spam și enervează', 'NU trimiți mesaje generice - personalizează fiecare comunicare', 'NU renunți după 2-3 încercări - persistența face diferența'],
      checklist: ['Programează follow-up-uri în CRM automat', 'Variază canalele: Apel → SMS → Email → WhatsApp', 'Personalizează fiecare mesaj cu detalii din conversații anterioare', 'Oferă valoare nouă la fiecare contact (proprietăți noi, info zonă)', 'Notează fiecare interacțiune în istoric', 'Setează reminder-e pentru next steps']
    }
  }, {
    id: 'system-flow',
    title: 'Cum Funcționează Sistemul',
    description: 'Înțelege statusurile și flow-ul leadurilor în CRM',
    icon: TrendingUpIcon,
    color: 'cyan',
    gradient: 'from-cyan-500 to-blue-600',
    content: {
      tips: ['🆕 LEAD NOU: Tocmai primit, contactează în 1-5 minute', '📞 DE APELAT: Programat pentru apel, respectă ora stabilită', '❌ NU RĂSPUNDE: 3+ încercări fără răspuns, încearcă alt interval', '✅ CALIFICARE: În proces de calificare, pune întrebările cheie', '👁️ VREA VIZIONARE: Calificat și interesat, programează rapid', '🚫 NU VREA VIZIONARE: Refuz sau necalificat, notează motivul', '⏸️ PAUZĂ: Lead valid dar nu e momentul, programează follow-up', '🎯 CALIFICAT: Gata pentru matching cu proprietăți'],
      warnings: ['Leadurile "Nou" sunt PRIORITATE MAXIMĂ - contactează imediat', 'Actualizează statusul după FIECARE interacțiune', 'Notează TOTUL în istoric - detaliile fac diferența']
    }
  }, {
    id: 'best-practices',
    title: 'Best Practices & Tehnici Pro',
    description: 'Strategii avansate de la top performeri',
    icon: AwardIcon,
    color: 'violet',
    gradient: 'from-violet-500 to-purple-600',
    content: {
      tips: ['🎯 RATA DE CONVERSIE: 20-30% din leaduri → vizionări → 40-50% din vizionări → contracte', '⚡ VITEZĂ: Leadurile contactate în <5 min au conversie de 8x mai mare', '📱 MULTICANAL: Combină apeluri + SMS + WhatsApp + Email pentru rezultate maxime', '🎤 VOICEMAIL PRO: "Bună ziua, sunt [Nume] de la [Agenție]. V-am sunat pentru cererea dumneavoastră de apartament. Vă sun din nou în 2 ore. Mulțumesc!"', '💬 SCRIPT WHATSAPP: "Bună ziua! Sunt [Nume], agent imobiliar. Am văzut cererea dumneavoastră pentru apartament în [Zonă]. Am 3 opțiuni perfecte pentru bugetul dumneavoastră. Când aveți 10 minute pentru o discuție scurtă? 😊"', '🎁 BONUS VALUE: Oferă ghiduri gratuite (checklist mutare, calculator costuri, ghid zonă)', '⭐ TESTIMONIALE: Trimite povești de succes ale altor clienți similari', '🔥 URGENȚĂ: "Proprietatea asta e foarte căutată, am programat deja 2 vizionări pentru mâine"'],
      checklist: ['Răspunde în <5 minute la leadurile noi (ideal: 1-2 minute)', 'Fă minim 7 touch-points înainte să renunți', 'Personalizează fiecare comunicare cu detalii specifice', 'Folosește CRM-ul pentru tracking complet', 'Setează reminder-e automate pentru follow-up', 'Analizează săptămânal: rata de răspuns, conversie, motive refuz', 'Testează diferite scripturi și ore de apel', 'Cere feedback de la leadurile pierdute']
    }
  }];
  const currentQuestData = quests[currentQuest];
  const handleCompleteQuest = () => {
    if (!completedQuests.includes(currentQuestData.id)) {
      setCompletedQuests([...completedQuests, currentQuestData.id]);
    }
    if (currentQuest < quests.length - 1) {
      setCurrentQuest(currentQuest + 1);
    }
  };
  const handlePrevious = () => {
    if (currentQuest > 0) {
      setCurrentQuest(currentQuest - 1);
    }
  };
  const progress = completedQuests.length / quests.length * 100;
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-id="element-9977">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-id="element-9978">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-9979">
          <div className="flex items-center justify-between" data-id="element-9980">
            <div className="flex items-center space-x-4" data-id="element-9981">
              <button onClick={() => navigate('/inchiriere/leaduri')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-9982">
                <XIcon className="w-6 h-6 text-slate-600" data-id="element-9983" />
              </button>
              <div data-id="element-9984">
                <h1 className="text-2xl font-bold text-[#132C53]" data-id="element-9985">
                  Tutorial Leaduri
                </h1>
                <p className="text-sm text-slate-600" data-id="element-9986">
                  Quest {currentQuest + 1} din {quests.length}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3" data-id="element-9987">
              <div className="text-right" data-id="element-9988">
                <p className="text-xs text-slate-500" data-id="element-9989">Progres</p>
                <p className="text-lg font-bold text-blue-600" data-id="element-9990">
                  {completedQuests.length}/{quests.length}
                </p>
              </div>
              <div className="w-16 h-16 relative" data-id="element-9991">
                <svg className="w-16 h-16 transform -rotate-90" data-id="element-9992">
                  <circle cx="32" cy="32" r="28" stroke="#e2e8f0" strokeWidth="6" fill="none" data-id="element-9993" />
                  <circle cx="32" cy="32" r="28" stroke="#3b82f6" strokeWidth="6" fill="none" strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`} strokeLinecap="round" className="transition-all duration-500" data-id="element-9994" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-9995">
                  <span className="text-sm font-bold text-blue-600" data-id="element-9996">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quest Progress Bar */}
      <div className="bg-white border-b border-slate-200" data-id="element-9997">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-9998">
          <div className="flex items-center space-x-2" data-id="element-9999">
            {quests.map((quest, index) => <Fragment key={quest.id} data-id="element-10000">
                <button onClick={() => setCurrentQuest(index)} className={`flex-1 h-2 rounded-full transition-all ${completedQuests.includes(quest.id) ? 'bg-gradient-to-r from-emerald-500 to-green-600' : index === currentQuest ? 'bg-gradient-to-r from-blue-500 to-indigo-600' : 'bg-slate-200'}`} data-id="element-10001" />
              </Fragment>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-10002">
        <AnimatePresence mode="wait" data-id="element-10003">
          <motion.div key={currentQuestData.id} initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -50
        }} transition={{
          duration: 0.3
        }} data-id="element-10004">
            {/* Quest Header */}
            <div className="text-center mb-12" data-id="element-10005">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} className={`w-24 h-24 mx-auto mb-6 bg-gradient-to-br ${currentQuestData.gradient} rounded-3xl flex items-center justify-center shadow-2xl`} data-id="element-10006">
                <currentQuestData.icon className="w-12 h-12 text-white" data-id="element-10007" />
              </motion.div>
              <h2 className="text-4xl font-bold text-[#132C53] mb-4" data-id="element-10008">
                {currentQuestData.title}
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-10009">
                {currentQuestData.description}
              </p>
            </div>

            {/* Content Cards */}
            <div className="space-y-6" data-id="element-10010">
              {/* Tips */}
              {currentQuestData.content.tips && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200" data-id="element-10011">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-10012">
                    <div className={`w-10 h-10 bg-gradient-to-br ${currentQuestData.gradient} rounded-xl flex items-center justify-center`} data-id="element-10013">
                      <LightbulbIcon className="w-5 h-5 text-white" data-id="element-10014" />
                    </div>
                    <h3 className="text-xl font-bold text-[#132C53]" data-id="element-10015">
                      Sfaturi Esențiale
                    </h3>
                  </div>
                  <div className="space-y-4" data-id="element-10016">
                    {currentQuestData.content.tips.map((tip, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.2 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-10017">
                        <CheckCircleIcon className={`w-5 h-5 text-${currentQuestData.color}-600 flex-shrink-0 mt-0.5`} data-id="element-10018" />
                        <p className="text-slate-700 leading-relaxed" data-id="element-10019">{tip}</p>
                      </motion.div>)}
                  </div>
                </motion.div>}

              {/* Warnings */}
              {currentQuestData.content.warnings && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-8 shadow-lg border-2 border-red-200" data-id="element-10020">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-10021">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center" data-id="element-10022">
                      <AlertCircleIcon className="w-5 h-5 text-white" data-id="element-10023" />
                    </div>
                    <h3 className="text-xl font-bold text-red-900" data-id="element-10024">
                      Atenție - Greșeli Comune
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-10025">
                    {currentQuestData.content.warnings.map((warning, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.4 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-white rounded-xl" data-id="element-10026">
                        <XIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-10027" />
                        <p className="text-red-900 font-medium leading-relaxed" data-id="element-10028">
                          {warning}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}

              {/* Checklist */}
              {currentQuestData.content.checklist && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 shadow-lg border-2 border-emerald-200" data-id="element-10029">
                  <div className="flex items-center space-x-3 mb-6" data-id="element-10030">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center" data-id="element-10031">
                      <ClipboardCheckIcon className="w-5 h-5 text-white" data-id="element-10032" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-900" data-id="element-10033">
                      Checklist Acțiuni
                    </h3>
                  </div>
                  <div className="space-y-3" data-id="element-10034">
                    {currentQuestData.content.checklist.map((item, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.6 + index * 0.1
                }} className="flex items-start space-x-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow cursor-pointer" data-id="element-10035">
                        <div className="w-5 h-5 border-2 border-emerald-500 rounded flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-10036">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-10037" />
                        </div>
                        <p className="text-emerald-900 font-medium leading-relaxed" data-id="element-10038">
                          {item}
                        </p>
                      </motion.div>)}
                  </div>
                </motion.div>}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12" data-id="element-10039">
              <button onClick={handlePrevious} disabled={currentQuest === 0} className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition-all ${currentQuest === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white text-slate-700 hover:bg-slate-50 shadow-md hover:shadow-lg'}`} data-id="element-10040">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-10041" />
                <span data-id="element-10042">Înapoi</span>
              </button>

              {currentQuest === quests.length - 1 ? <button onClick={() => {
              handleCompleteQuest();
              navigate('/inchiriere/leaduri');
            }} className="flex items-center space-x-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-xl hover:scale-105 transition-all" data-id="element-10043">
                  <AwardIcon className="w-5 h-5" data-id="element-10044" />
                  <span data-id="element-10045">Finalizează Tutorial</span>
                </button> : <button onClick={handleCompleteQuest} className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r ${currentQuestData.gradient} text-white hover:shadow-xl hover:scale-105 transition-all`} data-id="element-10046">
                  <span data-id="element-10047">Următorul Quest</span>
                  <ArrowRightIcon className="w-5 h-5" data-id="element-10048" />
                </button>}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}